import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface DataAdvisoryProps {
  onNext: () => void;
  onBack: () => void;
}

const DataAdvisory: FC<DataAdvisoryProps> = ({ onNext, onBack }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
            </button>
            <span className="font-bold text-brand-text dark:text-white">
              {isFr ? 'Données mobiles' : 'Mobile Data'}
            </span>
            <div className="w-20" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-5">
            <span className="material-symbols-outlined text-blue-600" style={{ fontSize: '32px' }}>signal_cellular_alt</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-brand-text dark:text-white mb-3">
            {isFr
              ? 'AidFone a-t-il besoin de données mobiles?'
              : 'Does AidFone need mobile data?'}
          </h2>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700 p-6 sm:p-8 mb-8">
          <p className="text-base text-brand-text dark:text-slate-300 leading-relaxed mb-5">
            {isFr
              ? "Oui. AidFone nécessite des données mobiles pour maintenir les fonctions de protection actives. Choisissez un forfait de 500 Mo/mois auprès de n'importe quel opérateur canadien. Cela couvre tout\u00a0: GPS, alertes et assistant vocal."
              : 'Yes. AidFone requires mobile data to keep safety features active. Get a 500 MB/month plan from any Canadian carrier. That covers everything — GPS, alerts, and the voice assistant.'}
          </p>
          <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
            <span className="material-symbols-outlined text-emerald-600 flex-shrink-0 mt-0.5" style={{ fontSize: '20px' }}>check_circle</span>
            <p className="text-sm text-emerald-800 dark:text-emerald-300 leading-relaxed">
              {isFr
                ? "Les appels téléphoniques n'utilisent aucune donnée — ils passent par le réseau vocal de votre opérateur, comme d'habitude."
                : "Phone calls use zero data — they use your carrier's voice network as normal."}
            </p>
          </div>
        </div>

        {/* Summary chips */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full border border-brand-border dark:border-slate-700 text-sm">
            <span className="material-symbols-outlined text-blue-500" style={{ fontSize: '18px' }}>sim_card</span>
            <span className="text-brand-text dark:text-slate-300 font-medium">500 MB/{isFr ? 'mois' : 'month'}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full border border-brand-border dark:border-slate-700 text-sm">
            <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: '18px' }}>phone_in_talk</span>
            <span className="text-brand-text dark:text-slate-300 font-medium">{isFr ? 'Appels = 0 donnée' : 'Calls = 0 data'}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full border border-brand-border dark:border-slate-700 text-sm">
            <span className="material-symbols-outlined text-amber-500" style={{ fontSize: '18px' }}>cell_tower</span>
            <span className="text-brand-text dark:text-slate-300 font-medium">{isFr ? 'Tout opérateur' : 'Any carrier'}</span>
          </div>
        </div>

        {/* Continue button */}
        <div className="text-center">
          <button
            onClick={onNext}
            className="px-8 py-3.5 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-all shadow-lg shadow-brand-accent/30 text-base"
          >
            {isFr ? 'Compris — continuer' : 'Understood — continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataAdvisory;
